import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {emitTrackEvent} from 'common/util/dom';
import {SelectTheme} from 'common/components/select/constants';
import {RetentionTrackingEvents as Events} from 'common/report/retention/util/constants';
import {MenuChangeEvent} from 'common/components/select/types';
import {RetentionUnboundedModeType} from 'common/report/retention/util/types';

import {template} from './template';
import css from './index.inline.styl';

type RetentionQuerySettingsAttrs = {
  'counting-method': string;
  'retention-type': string;
};

type RetentionQuerySettingsState = {
  isSelectMenuHovered: boolean;
  isSelectMenuOpen: boolean;
};

const FREQUENCY_HELP_DOCS_LINK = `\n[Learn more](https://docs.mixpanel.com/docs/analysis/reports/retention#frequency-view)`;
const FREQUENCY_COUNTING_METHOD = Object.freeze({
  title: `Cumulative vs Non-cumulative Frequency`,
  items: [
    {
      label: `Cumulative Frequency`,
      value: `cumulative`,
      tooltip: {text: `Measures users who did the event in at least X unique intervals.${FREQUENCY_HELP_DOCS_LINK}`},
    },
    {
      label: `Non-cumulative Frequency`,
      value: `non-cumulative`,
      tooltip: {text: `Measures users who did the event in exactly X unique intervals.${FREQUENCY_HELP_DOCS_LINK}`},
    },
  ],
});

const DEFAULT_COLLAPSIBLE_OPEN = true;

export type RetentionQuerySettingsUpdate = CustomEvent<{
  retentionCumulative?: boolean;
  unboundedMode?: RetentionUnboundedModeType;
}>;

export class RetentionQuerySettings extends Component<RetentionQuerySettingsState, RetentionQuerySettingsAttrs> {
  static get attrsSchema(): AttrsSchema<RetentionQuerySettingsAttrs> {
    return {
      ...super.attrsSchema,
      'counting-method': `string`,
      'retention-type': `string`,
    };
  }

  get config() {
    return {
      css,
      useShadowDom: true,
      template,
      defaultState: {
        isSelectMenuHovered: false,
        isSelectMenuOpen: false,
      },
      helpers: {
        DEFAULT_COLLAPSIBLE_OPEN,
        getSelectAttrs: () => ({
          elref: `retentionQuerySelect`,
          sections: [
            {
              ...FREQUENCY_COUNTING_METHOD,
              items: FREQUENCY_COUNTING_METHOD.items.map((i) => ({
                ...i,
                isSelected: i.value === this.attr(`counting-method`),
              })),
            },
          ],
          'always-show-caret': this.state.isSelectMenuHovered || this.state.isSelectMenuOpen,
          icon: {leftIcon: `column-select`, rightIcon: `double-caret`},
          theme: SelectTheme.Tertiary,
          'menu-width': 204,
        }),
        handleCollapsibleQuerySectionChange: (ev: CustomEvent) => {
          const isBlockOpen = ev.detail.state === `open`;

          emitTrackEvent(this, {
            eventName: Events.ClickedAdvancedQuerySettings,
            eventPrefix: `mp-query-builder`,
            props: {
              report: `retention`,
              Action: isBlockOpen ? `expand` : `collapse`,
            },
          });
        },
        handleSelectChange: (ev: CustomEvent) => {
          const {value} = ev.detail.item;
          const isAddiction = this.attr(`retention-type`) === `addiction`;
          const querySettingsUpdateEvent: RetentionQuerySettingsUpdate = new CustomEvent(`change`, {
            detail: {
              ...(isAddiction
                ? {retentionCumulative: value === `cumulative`}
                : {
                    unboundedMode: value,
                  }),
            },
          });
          this.update({isSelectMenuOpen: false, isSelectMenuHovered: false});
          this.dispatchEvent(querySettingsUpdateEvent);
        },
      },
    };
  }

  handleSelectMenuChange = (ev: MenuChangeEvent) => {
    this.update({isSelectMenuOpen: ev.detail.isOpen});
  };

  handleSelectMenuMouseEnter = () => {
    this.update({isSelectMenuHovered: true});
  };

  handleSelectMenuMouseLeave = () => {
    this.update({isSelectMenuHovered: false});
  };
}

type RetentionQuerySettingsJSX = {
  attrs: RetentionQuerySettingsAttrs;
  on: {change: (ev: RetentionQuerySettingsUpdate) => void};
};

export const RetentionQuerySettingsEl = defineAndMakeJsxFactory<RetentionQuerySettingsJSX>(
  `retention-query-settings`,
  RetentionQuerySettings,
);
export const RetentionQuerySettingsReact = panel2react<RetentionQuerySettingsJSX>(
  `retention-query-settings`,
  RetentionQuerySettings,
);
